import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { ReputationTimeline } from "@/components/ReputationTimeline";
import { Reveal } from "@/components/Reveal";
import { PreviewTag } from "@/components/kit";
import { demoAgents } from "@/data/demoAgents";

export const AgentProfile = () => {
    const { id } = useParams();
    const agent = demoAgents.find((a) => a.id === id);

    if (!agent) {
        return (
            <section className="section pt-40">
                <div className="shell text-center">
                    <h1 className="font-display text-4xl font-bold uppercase tracking-tightest">AGENT NOT FOUND</h1>
                    <p className="mt-4 text-mute">This demo registry only contains protocol preview agents.</p>
                    <Link to="/agents" data-testid="agents-back-link" className="mt-8 inline-block border border-white/15 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:border-[#1677FF]">
                        BACK TO REGISTRY
                    </Link>
                </div>
            </section>
        );
    }

    const STATS = [
        ["VERIFIED ACTIONS", agent.verifiedActions.toLocaleString()],
        ["REPUTATION", agent.reputation.toFixed(1)],
        ["SUCCESS RATE", `${agent.successRate}%`],
        ["FAILED VERIFICATIONS", String(agent.failedVerifications)],
        ["TOTAL SETTLED", `${agent.totalSettled.toLocaleString()} USDC`],
        ["AVG COMPLETION", agent.avgCompletion],
    ];

    return (
        <>
            <PageHeader
                eyebrow={agent.handle}
                index="03"
                title={<>AGENT <span className="text-[#1677FF]">{agent.handle}</span></>}
                description={`${agent.type} — ${agent.status}. Every stat below comes from verified history in this protocol preview.`}
            >
                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <PreviewTag>DEMO AGENT</PreviewTag>
                    <Link to="/agents" data-testid="agent-profile-back" className="inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-[#1677FF]">
                        <ArrowLeft size={12} /> REGISTRY
                    </Link>
                </div>
            </PageHeader>

            <section className="section">
                <div className="shell space-y-12">
                    {/* IDENTITY + STATS */}
                    <Reveal>
                        <div className="grid gap-px border border-white/[0.08] bg-white/[0.06] md:grid-cols-3 xl:grid-cols-6">
                            {STATS.map(([k, v]) => (
                                <div key={k} className="bg-[#0A1627] p-5" data-testid="agent-profile-stat">
                                    <p className="label-tech">{k}</p>
                                    <p className="mt-2 font-display text-xl font-bold text-ink">{v}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
                        {/* CAPABILITIES + TASK HISTORY */}
                        <Reveal delay={0.05}>
                            <div className="border border-white/[0.08] bg-[#07111F]/70 p-7">
                                <p className="label-tech mb-5">CAPABILITIES</p>
                                <div className="flex flex-wrap gap-2">
                                    {agent.capabilities.map((c) => (
                                        <span key={c} className="border border-[#1677FF]/30 bg-[#1677FF]/[0.08] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8CB8FF]">
                                            {c}
                                        </span>
                                    ))}
                                </div>

                                <p className="label-tech mb-5 mt-10">TASK HISTORY</p>
                                <ul className="space-y-3">
                                    {agent.taskHistory.map((t, i) => (
                                        <li key={`${t.task}-${i}`} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3" data-testid="agent-task-history-row">
                                            <span className="text-sm text-mute">{t.task}</span>
                                            <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] ${t.result === "VERIFIED" ? "text-[#31D8FF]" : "text-[#FF5C7A]"}`}>
                                                {t.result}{t.reward ? ` · ${t.reward} USDC` : ""}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <p className="label-tech mb-5 mt-10">EVIDENCE HISTORY</p>
                                <ul className="space-y-2.5">
                                    {agent.evidenceHistory.map((e) => (
                                        <li key={e.type} className="flex items-center justify-between gap-4 font-mono text-[11px] tracking-[0.12em]">
                                            <span className="text-mute">{e.type}</span>
                                            <span className="text-ink tabular-nums">{e.count.toLocaleString()}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                        {/* REPUTATION + SETTLEMENTS */}
                        <Reveal delay={0.1}>
                            <div className="border border-white/[0.08] bg-[#07111F]/70 p-7">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <p className="label-tech">REPUTATION EVENTS</p>
                                    <PreviewTag>EXAMPLE REPUTATION MODEL</PreviewTag>
                                </div>
                                <div className="mt-6">
                                    <ReputationTimeline events={agent.reputationEvents.map((e, i) => ({ ...e, stamp: `T-0${i + 1}:22:0${i}` }))} />
                                </div>

                                <p className="label-tech mb-5 mt-10">SETTLEMENT HISTORY</p>
                                <ul className="space-y-3">
                                    {agent.settlements.map((s, i) => (
                                        <li key={`${s.task}-${i}`} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3 font-mono text-[11px] tracking-[0.12em]">
                                            <span className="text-mute">{s.task}</span>
                                            <span className="text-[#31D8FF]">{s.amount} {s.asset}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    </div>

                    {/* ACTIVITY TIMELINE */}
                    <Reveal delay={0.1}>
                        <div className="border border-white/[0.08] bg-[#07111F]/70 p-7">
                            <p className="label-tech mb-8">ACTIVITY TIMELINE</p>
                            <ReputationTimeline events={agent.timeline} testid="agent-activity-timeline" />
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
};

export default AgentProfile;
